import React, { useState, useEffect, useCallback } from "react";
import * as Mui from "@mui/material";
import { useForm } from "react-hook-form";
import { themes } from "services/constants";
import dayjs from "dayjs";
import { CalendarMonth, Info } from "@mui/icons-material";
import { Loader } from "components/Loader";
import { showToast } from "services/utils/Status";
import { minimum3Characters } from "services/constants/ErrorMessages";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { format, parse } from "date-fns";
import "./ButtonGroup.css";
import { customGetAPI } from "services/utils/CustomAPI";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import sessionHandling from "services/utils/notificationUtils";
import moment from "moment";
import { formKeyText, tooltipStyleProps } from "services/constants/Styles";

export const ApplyLeave = ({ handleClose }) => {
  const { domain, token, userDetails, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer
  );
  const {
    result: employeeNameOptions,
    loading,
    setLoad: reCallApi,
  } = useFetch(
    `${domain}list/employee?user_id=${userDetails.id}&company_id=${topbarCompanyId}`
  );
  const [checkboxChecked, setcheckboxChecked] = useState(false);
  const [checked, setChecked] = useState(false);
  const [firstHalfChecked, setFirstHalfChecked] = useState(false);
  const [secondHalfChecked, setSecondHalfChecked] = useState(false);
  const [employeeName, setEmployeeName] = useState("");
  const [leaveTypeDetails, setLeaveTypeDetails] = useState([]);
  const [leaveDate, setLeaveDate] = useState([]);
  const [filteredLabel, setFilteredLabel] = useState("");
  const [filteredValue, setFilteredValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [leaveStartDate, setLeaveStartDate] = useState("");
  const [leaveEndDate, setLeaveEndDate] = useState("");
  const [buttonLoader, setButtonLoader] = useState(false);
  const [leaveTypeOptions, setLeeaveTypeOptions] = useState([]);
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
    }
  }, [reCallApi, topbarCompanyId]);

  const { register, handleSubmit, reset, setValue, watch, trigger, formState } =
    useForm({ mode: "onBlur" });
  const { errors } = formState;

  const checkFromDate = watch("from_date");
  const checkToDate = watch("to_date");

  const formatDate = (dateString) => {
    const date = parse(dateString, "dd-MM-yyyy", new Date());
    const formattedDate = format(date, "yyyy-MM-dd");
    return formattedDate;
  };

  const selectedShiftBasedShifts = useCallback(
    async (id) => {
      const res = await customGetAPI(`${domain}leavetypefilter/?user_id=${id}`);
      if (res.ok) {
        setLeeaveTypeOptions(res.responseDetails);
      }
    },
    [domain]
  );

  const validateStartDate = (value) => {
    if (!value) {
      return "Start date is required.";
    }
  };

  const validateEndDate = (value) => {
    if (!endDate) {
      return "End date is required.";
    } else if (startDate && endDate < startDate) {
      return "End date cannot be before the start date";
    }
    return true;
  };

  const handleClearButtonClick = () => {
    reset();
    setcheckboxChecked(false);
    setChecked(false);
    setFirstHalfChecked(false);
    setSecondHalfChecked(false);
    setSecondHalfChecked(false);
    const today = dayjs().format("YYYY-MM-DD");
    setStartDate(today);
    setEndDate(today);
    setValue("from_date", today);
    setValue("to_date", today);
    setEmployeeName("");
  };
  const selectedLeave = leaveTypeOptions?.find(
    (item) => item.leave_type === watch("leave_type")
  );
  const availableLeave = selectedLeave?.available_leave ?? 0;
  const creditedLeave = selectedLeave?.credited_leave ?? 0;
  const usedLeave = selectedLeave?.used_leave ?? 0;

  const onSubmit = async (data) => {
    const currentLeaveType = leaveTypeOptions?.filter((leave) => {
      return leave.leave_type === data.leave_type;
    });

    setButtonLoader(true);
    try {
      const halfDay = firstHalfChecked || secondHalfChecked;
      const whichHalf = firstHalfChecked
        ? "1st"
        : secondHalfChecked
        ? "2nd"
        : null;
      const response = await fetch(`${domain}create/leave-request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          ...data,
          is_half_day: halfDay,
          half_day_period: whichHalf,
          user: watch("selected_user_id"),
          status: "Pending",
          applied_date: new Date().toISOString().split("T")[0],
          createdby: userDetails.first_name,
          createdat: new Date().toISOString(),
          modifiedby: userDetails.first_name,
          modifiedat: new Date().toISOString(),
          leave_type_id: currentLeaveType[0].id,
          action: "Approved",
          approver_id: userDetails.id,
        }),
      });
      const res = await response.json();
      if (response.ok) {
        showToast(
          "success",
          `You created ${
            employeeName?.user_name
          } leave request on ${checkFromDate
            .split("-")
            .reverse()
            .join("-")} to ${checkToDate
            .split("-")
            .reverse()
            .join("-")} and it has been approved.`
        );
        reset();
        setEmployeeName("");
        setLeaveTypeDetails([]);
        setLeeaveTypeOptions([]);
        setcheckboxChecked(false);
        setChecked(false);
        setFirstHalfChecked(false);
        setSecondHalfChecked(false);
        if (
          new Date(leaveStartDate).getFullYear() === new Date().getFullYear() ||
          new Date(leaveEndDate).getFullYear() === new Date().getFullYear()
        ) {
          const today = dayjs().format("YYYY-MM-DD");
          setStartDate(today);
          setEndDate(today);
        } else {
          setStartDate(leaveStartDate);
          setEndDate(leaveEndDate);
        }
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error(res.error);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      setButtonLoader(false);
    }
  };

  useEffect(() => {
    const fetchLeaveDate = async () => {
      try {
        const response = await fetch(`${domain}leaves-years/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });
        const res = await response.json();
        if (response.ok) {
          const leaveDate = res.map((value) => ({
            label: value.name,
            start_date: value.start_date,
            end_date: value.end_date,
            filteredValue: `${value.start_date} - ${value.end_date}`,
          }));
          const today = dayjs().format("YYYY-MM-DD");
          const sDate = formatDate(res[0]?.start_date);
          const eDate = formatDate(res[0]?.end_date);
          setLeaveDate(leaveDate);
          setFilteredLabel(res[0]?.name);
          setFilteredValue(`${res[0]?.start_date} - ${res[0]?.end_date}`);
          setLeaveStartDate(sDate);
          setLeaveEndDate(eDate);
          if (
            new Date(
              res[0]?.start_date.split("-").reverse().join("-")
            ).getFullYear() === new Date().getFullYear() ||
            new Date(
              res[0]?.end_date.split("-").reverse().join("-")
            ).getFullYear() === new Date().getFullYear()
          ) {
            setStartDate(today);
            setEndDate(today);
          } else {
            setStartDate(sDate);
            setEndDate(eDate);
          }
          setValue("from_date", today);
          setValue("to_date", today);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(res.error);
        }
      } catch (error) {
        showToast("error", error.message);
      }
    };
    fetchLeaveDate();
  }, [domain, setValue, token]);

  const handleChangeLeavePeriod = (event, value) => {
    const today = dayjs().format("YYYY-MM-DD");
    if (value && value.filteredValue) {
      setFilteredLabel(value.label);
      setFilteredValue(value.filteredValue);
      setStartDate(formatDate(value.start_date));
      setEndDate(formatDate(value.end_date));
      setLeaveStartDate(formatDate(value.start_date));
      setLeaveEndDate(formatDate(value.end_date));
      if (
        new Date(
          value.start_date.split("-").reverse().join("-")
        ).getFullYear() === new Date().getFullYear() ||
        new Date(
          value.end_date.split("-").reverse().join("-")
        ).getFullYear() === new Date().getFullYear()
      ) {
        let startDateConvertions = moment(new Date()).format("DD-MM-YYYY");
        setStartDate(formatDate(startDateConvertions));
        setEndDate(formatDate(startDateConvertions));
        setValue("from_date", formatDate(startDateConvertions));
        setValue("to_date", formatDate(startDateConvertions));
      } else {
        setStartDate(formatDate(value.start_date));
        setEndDate(formatDate(value.end_date));
        setValue("from_date", formatDate(value.start_date));
        setValue("to_date", formatDate(value.end_date));
      }
    } else {
      setFilteredLabel("");
      setFilteredValue("");
      setLeaveStartDate("");
      setLeaveEndDate("");
      setFilteredLabel("");
      setFilteredValue("");
      setLeaveStartDate(today);
      setLeaveEndDate(today);
      setStartDate(today);
      setEndDate(today);
    }
  };

  const handleCheckboxChange = (event) => {
    const checked = event.target.checked;
    setcheckboxChecked(checked);
    const today = dayjs().format("YYYY-MM-DD");
    const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");

    if (checked) {
      setStartDate(tomorrow);
      setEndDate(tomorrow);
      setValue("from_date", tomorrow);
      setValue("to_date", tomorrow);
      setSecondHalfChecked(false);
      setFirstHalfChecked(false);
      setChecked(false);
    } else {
      setStartDate(today);
      setEndDate(today);
      setValue("from_date", today);
      setValue("to_date", today);
      setSecondHalfChecked(false);
      setFirstHalfChecked(false);
      setChecked(false);
    }
  };

  const handleCheckboxChanged = (event) => {
    const isChecked = event.target.checked;
    setChecked(isChecked);
    if (isChecked) {
      setcheckboxChecked(false);
      setFirstHalfChecked(true);
    } else {
      setcheckboxChecked(false);
    }
  };

  const handleStartDateChange = (event) => {
    const date = event.target.value;
    setStartDate(date);
    setEndDate(date);
    setValue("from_date", date);
    setValue("to_date", date);
  };
  const handleEndDateChange = (event) => {
    const date = event.target.value;
    setEndDate(date);
  };

  const handleSetValue = () => {
    setValue("from_date", startDate);
    setValue("to_date", endDate);
  };

  const handleFirstHalfCheckboxChanged = (event) => {
    const checked = event.target.checked;
    if (checked) {
      setSecondHalfChecked(false);
      setcheckboxChecked(false);
      setFirstHalfChecked(checked);
    } else {
      setFirstHalfChecked(!checked);
    }
  };

  const handleSecondHalfCheckboxChanged = (event) => {
    const checked = event.target.checked;
    if (checked) {
      setFirstHalfChecked(false);
      setcheckboxChecked(false);
      setSecondHalfChecked(checked);
    } else {
      setSecondHalfChecked(!checked);
    }
  };

  const updateUserName = (event, data) => {
    if (data) {
      setValue("user_name", data?.user_name);
      setValue("selected_user_id", data?.user_id);
      setEmployeeName(data);
      setLeaveTypeDetails([]);
      setValue("leave_type", "");
      selectedShiftBasedShifts(data?.user_id);
      setLeeaveTypeOptions([]);
      setShowAutoCompleteAvathar(false);
    } else {
      setShowAutoCompleteAvathar(true);
    }
  };

  const updateLeaveTypeDetails = (event, data) => {
    if (data) {
      setLeaveTypeDetails(data?.label);
      setValue("leave_type", data?.label);
      setValue("leave_type_id", data?.id);
    }
  };

  return (
    <>
      {loading ? (
        <Mui.Box>
          <Loader info="Loading..." />
        </Mui.Box>
      ) : (
        <React.Fragment>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Mui.Grid
              container
              sx={{
                paddingLeft: "10%",
                paddingRight: "10%",
                paddingTop: "5%",
              }}
            >
              <Mui.Grid
                container
                sx={{
                  padding: 1,
                  backgroundColor: themes.primary,
                  borderRadius: "10px 10px 0 0",
                  border: `2px solid ${themes.sidebarDivider}`,
                  borderBottom: "none",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Mui.Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 800,
                    font: "bold",
                    textAlign: "center",
                  }}
                >
                  Apply Leave
                </Mui.Typography>
              </Mui.Grid>
              <Mui.Grid
                container
                sx={{
                  border: `2px solid ${themes.sidebarDivider}`,
                  padding: 2,
                  borderRadius: "0 0 10px 10px",
                }}
              >
                <Mui.Grid
                  container
                  xs={10.3}
                  justifyContent={"space-between"}
                  sx={{
                    paddingTop: 2,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                  }}
                >
                  <Mui.Autocomplete
                    disablePortal
                    id="combo-box-demo"
                    size="small"
                    value={
                      leaveDate?.find(
                        (option) => option?.filteredValue === filteredValue
                      ) || ""
                    }
                    options={leaveDate}
                    style={{
                      width: 305,
                    }}
                    getOptionLabel={(option) =>
                      option?.filteredValue ?? filteredValue ?? ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.filteredValue === value?.filteredValue
                    }
                    filterOptions={(options, state) => {
                      return options.filter((option) =>
                        option.filteredValue
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase())
                      );
                    }}
                    renderOption={(props, option) => {
                      return <li {...props}>{option.filteredValue}</li>;
                    }}
                    ListboxProps={{
                      style: {
                        maxHeight: 150,
                      },
                    }}
                    onChange={handleChangeLeavePeriod}
                    renderInput={(params) => (
                      <Mui.TextField
                        {...params}
                        sx={{ background: themes.whiteColor }}
                        placeholder="Select leave period"
                        label={filteredLabel ? filteredLabel : "Leave Period"}
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <CalendarMonth
                                style={{
                                  margin: "0 8px",
                                  color: "#a6a6a6",
                                }}
                              />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Mui.Grid>
                <Mui.Grid container sx={{ paddingTop: 1 }}>
                  <Mui.Grid xs={4}>
                    <Mui.Typography sx={formKeyText}>
                      Employee Name &nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title="Select the employee applying for leave."
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid xs={0.5}></Mui.Grid>
                  <Mui.Grid xs={5.8}>
                    <CustomAutoCompleteWithLogo
                      dataList={employeeNameOptions?.map((obj) => ({
                        ...obj,
                        label: obj?.user_name,
                        image: obj?.image_url,
                      }))}
                      rules={{
                        ...register("user_name", {
                          required: "Employee name is required",
                        }),
                      }}
                      selectedValue={employeeName}
                      updateDataFunction={updateUserName}
                      placeHolder="Select employee name"
                      errorMessage={
                        errors?.user_name?.message
                          ? errors?.user_name?.message
                          : ""
                      }
                      textBoxError={errors?.user_name ? true : false}
                      showAvathar={showAutoCompleteAvathar}
                      setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
                    />
                  </Mui.Grid>
                </Mui.Grid>
                <Mui.Grid container sx={{ paddingTop: 1 }}>
                  <Mui.Grid xs={4}>
                    <Mui.Typography sx={formKeyText}>
                      Leave Type&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title=" Choose the category from the exixsting leave types."
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid xs={0.5}></Mui.Grid>
                  <Mui.Grid xs={5.8}>
                    <CustomAutoComplete
                      dataList={leaveTypeOptions?.map((obj) => ({
                        ...obj,
                        label: obj?.leave_type,
                      }))}
                      rules={{
                        ...register("leave_type", {
                          required: "Leave type is required",
                        }),
                      }}
                      selectedValue={leaveTypeDetails}
                      updateDataFunction={updateLeaveTypeDetails}
                      placeHolder="Select leave type"
                      errorMessage={
                        errors?.leave_type?.message
                          ? errors?.leave_type?.message
                          : ""
                      }
                      textBoxError={errors?.leave_type ? true : false}
                    />
                  </Mui.Grid>
                </Mui.Grid>
                <Mui.Grid container sx={{ paddingTop: 1 }}>
                  <Mui.Grid xs={4}>
                    <Mui.Typography sx={formKeyText}>
                      Credited Leaves&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title="Auto-displays the credited leave balance."
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid xs={0.5}></Mui.Grid>
                  <Mui.Grid xs={0.6} sx={{ mt: 2 }}>
                    <Mui.Typography>{creditedLeave}</Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid xs={2.3} sx={{ paddingTop: 0.2 }}>
                    <Mui.Typography sx={formKeyText}>
                      Available Leaves&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title="Auto-displays the remaining leave balance."
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid xs={0.6} sx={{ mt: 2 }}>
                    <Mui.Typography>{availableLeave}</Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid xs={2} sx={{ paddingTop: 0.2 }}>
                    <Mui.Typography sx={formKeyText}>
                      Used Leaves&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title="Auto-displays the used leave balance."
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid xs={0.5} sx={{ mt: 2 }}>
                    <Mui.Typography>{usedLeave}</Mui.Typography>
                  </Mui.Grid>
                </Mui.Grid>

                <Mui.Grid container sx={{ paddingTop: 1 }}>
                  <Mui.Grid xs={4.5}></Mui.Grid>
                  <Mui.Grid xs={5}>
                    <Mui.Grid container>
                      <Mui.Grid xs={6}>
                        <Mui.FormControlLabel
                          control={<Mui.Checkbox />}
                          label="Only For Tomorrow"
                          checked={checkboxChecked}
                          onChange={handleCheckboxChange}
                          onClick={handleSetValue}
                        />
                      </Mui.Grid>
                      <Mui.Grid xs={6}>
                        <Mui.FormControlLabel
                          control={
                            <Mui.Checkbox
                              checked={checked}
                              onChange={handleCheckboxChanged}
                            />
                          }
                          label="Half Day"
                        />
                      </Mui.Grid>
                    </Mui.Grid>
                  </Mui.Grid>
                  {checked && (
                    <Mui.Grid container sx={{ paddingTop: 1 }}>
                      <Mui.Grid xs={4.5}></Mui.Grid>
                      <Mui.Grid xs={5}>
                        <Mui.Grid container>
                          <Mui.Grid xs={6}>
                            <Mui.FormControlLabel
                              control={
                                <Mui.Checkbox
                                  checked={firstHalfChecked}
                                  onChange={handleFirstHalfCheckboxChanged}
                                />
                              }
                              label="1st-half"
                            />
                          </Mui.Grid>
                          <Mui.Grid item xs={4}>
                            <Mui.FormControlLabel
                              control={
                                <Mui.Checkbox
                                  checked={secondHalfChecked}
                                  onChange={handleSecondHalfCheckboxChanged}
                                />
                              }
                              label="2nd-half"
                            />
                          </Mui.Grid>
                        </Mui.Grid>
                      </Mui.Grid>
                    </Mui.Grid>
                  )}
                </Mui.Grid>
                <Mui.Grid container>
                  <Mui.Grid xs={4}>
                    <Mui.Typography sx={formKeyText}>
                      Start Date&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title="Choose the first day of your leave period."
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid xs={0.5}> </Mui.Grid>
                  <Mui.Grid xs={5.8}>
                    <Mui.TextField
                      size="small"
                      type="date"
                      value={startDate}
                      id="from_date"
                      inputProps={{
                        min: leaveStartDate,
                        max: leaveEndDate,
                      }}
                      {...register("from_date", {
                        required: "start date is required.",
                        validate: validateStartDate,
                      })}
                      error={errors.from_date}
                      onChange={handleStartDateChange}
                      helperText={errors.from_date && errors.from_date.message}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      disabled={checkboxChecked}
                      fullWidth
                    />
                  </Mui.Grid>
                </Mui.Grid>
                <Mui.Grid container sx={{ paddingTop: 1 }}>
                  <Mui.Grid xs={4}>
                    <Mui.Typography sx={formKeyText}>
                      End Date&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title="Choose the last day of your leave period."
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid xs={0.5}></Mui.Grid>
                  <Mui.Grid xs={5.8}>
                    <Mui.TextField
                      size="small"
                      type="date"
                      id="to_date"
                      value={endDate}
                      inputProps={{
                        min: leaveStartDate,
                        max: leaveEndDate,
                      }}
                      {...register("to_date", {
                        required: "End date is required.",
                        validate: {
                          dateNotBeforeStart: validateEndDate,
                        },
                      })}
                      onChange={handleEndDateChange}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      disabled={checked ? checked : false || checkboxChecked}
                      error={errors.to_date}
                      helperText={errors.to_date && errors.to_date.message}
                      fullWidth
                    />
                  </Mui.Grid>
                </Mui.Grid>
                <br />
                <Mui.Grid container sx={{ paddingTop: 1 }}>
                  <Mui.Grid xs={4}>
                    <Mui.Typography sx={formKeyText}>
                      Reason for Leave&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title="Mention the reason for applying."
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid xs={0.5}></Mui.Grid>
                  <Mui.Grid xs={5.8}>
                    <Mui.TextField
                      fullWidth
                      id="description"
                      {...register("description", {
                        required: "Reason for leave is required",
                        validate: {
                          minLengthNoSpaces: (value) =>
                            value?.trim().length < 3 && value !== ""
                              ? minimum3Characters
                              : true,
                        },
                      })}
                      placeholder="Provide a reason for your leave"
                      multiline
                      minRows={2}
                      error={errors.description}
                      helperText={
                        errors.description && errors.description.message
                      }
                      onBlur={() => {
                        trigger("description");
                      }}
                    />
                  </Mui.Grid>
                </Mui.Grid>
                <br />

                <Mui.Grid
                  container
                  xs={10.3}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                    paddingTop: 1,
                  }}
                >
                  <CustomButton
                    actionFuntion={handleClearButtonClick}
                    actionName="Clear"
                    typeName="button"
                  />
                  &nbsp;&nbsp;
                  {buttonLoader ? (
                    <CustomButton
                      disableAction={buttonLoader}
                      type="submit"
                      actionName="Approve"
                    />
                  ) : (
                    <Mui.Button
                      sx={{
                        width: "100px",
                        height: "36px",
                        fontWeight: 600,
                        display: "block",
                        backgroundColor: themes.primaryButton,
                        "&:hover": {
                          backgroundColor: themes.primaryButton,
                        },
                        textTransform: "capitalize",
                        color: themes.headLine,
                      }}
                      type="submit"
                      variant="contained"
                    >
                      Approve
                    </Mui.Button>
                  )}
                </Mui.Grid>
              </Mui.Grid>
            </Mui.Grid>
          </form>
        </React.Fragment>
      )}
    </>
  );
};
